<template>
  <BillingProductsAgreement
    v-if="showBillingProductAgreement"
    :billing-products="this.$store.state.order.billingProducts"
    :order-id="this.$route.params.orderID"
    @closeModal="toggleShowBillingProductModal"
    @continue="continueAddToCart"
  />

  <section
    class="fixed w-full h-full top-0 bottom-0 left-0 right-0 z-[70] flex justify-center items-center p-4 lg:p-0"
    @click="closeModal($event)"
  >
    <div
      class="modal-content flex flex-col w-full max-w-full lg:max-w-[1048px] h-auto max-h-[85vh] rounded border border-grays-400 bg-white shadow-popup overflow-auto"
    >
      <!-- Top section: Image + Product Info (side by side on desktop) -->
      <div class="flex flex-col lg:flex-row lg:h-[631px]">
        <div
          class="relative w-full lg:w-[479px] h-[200px] lg:h-full flex flex-none rounded rounded-b-0 pt-4 lg:pt-0"
        >
          <button-like
            :like="product.favourite"
            class="absolute z-20 top-[10px] right-[10px]"
            @toggle-like="toggleLike(product)"
          />
          <button
            class="absolute top-2 left-2 w-7 h-7 flex lg:hidden justify-center items-center rounded z-20"
            @click="toggleIsModalVisible"
          >
            <span class="material-symbols-rounded text-xl text-primary-700"
              >close</span
            >
          </button>
          <div
            v-if="soldOut"
            class="absolute w-full h-full bg-white bg-opacity-75 z-50 flex items-center justify-center text-primary-700 text-2xl"
          >
            אזל מהמלאי
          </div>
          <img
            v-if="!product.product.images.length"
            :src="`https://images.mishnatyosef.org/images/items/${image}`"
            alt="Product"
            class="w-full h-full object-contain rounded"
          />
          <swiper
            v-else
            :modules="modules"
            :navigation="{ nextEl: '.arrow-left', prevEl: '.arrow-right' }"
            :pagination="true"
            class="mySwiper rounded rounded-l-0 w-full h-full"
          >
            <swiper-slide
              v-for="image in imageList"
              :key="image"
              class="!w-full h-full flex items-center justify-center lg:min-h-[645px]"
            >
              <img
                :src="`https://images.mishnatyosef.org/images/items/${image.filename}.${image.extension}`"
                alt="item"
                class="w-full h-full object-contain"
              />
            </swiper-slide>
            <div
              class="absolute w-full h-fit top-0 bottom-0 m-auto flex justify-between items-center z-20"
            >
              <button class="arrow-right arrow">
                <span class="material-symbols-rounded text-grays-700"
                  >chevron_left</span
                >
              </button>
              <button class="arrow-left arrow">
                <span class="material-symbols-rounded text-grays-700"
                  >chevron_right</span
                >
              </button>
            </div>
          </swiper>
        </div>
        <div class="overflow-scroll flex flex-col flex-1">
          <div
            class="flex flex-none justify-end w-full h-fit py-[14px] px-[6px]"
          >
            <button
              class="hidden w-7 h-7 lg:flex justify-center items-center rounded"
              @click="toggleIsModalVisible"
            >
              <span class="material-symbols-rounded text-xl text-primary-700"
                >close</span
              >
            </button>
          </div>
          <div
            class="flex flex-1 flex-col p-4 lg:px-[8.5px] lg:pr-[28px] lg:py-[29px] mt-0 lg:mt-[41px]"
          >
            <div class="flex flex-col lg:w-[273px]">
              <p class="text-primary-700 text-xl font-bold">
                {{ product?.name }}
              </p>
              <span
                v-if="product.product.item_tag"
                :title="product.product.item_tag.description"
                class="inline-block bg-grays-300 text-grays-700 text-xs font-medium rounded px-2 py-[2px] mt-1 cursor-default"
                >{{ product.product.item_tag.tag }}</span
              >
              <p class="text-grays-700 text-lg font-medium my-[4px]">
                {{ computedUnit }}
              </p>
              <p class="text-grays-600 text-lg font-regular">
                {{ description }}
              </p>
            </div>
            <div
              v-if="product.details"
              class="flex flex-row gap-[20px] mt-[28px]"
            >
              <div class="flex flex-col gap-[4px]">
                <p class="text-grays-600 text-sm font-medium">פרטים</p>
                <p class="text-grays-900 text-base font-regular">
                  {{ product.details }}
                </p>
              </div>
              <div
                v-if="
                  product.manufacturer &&
                  (product.manufacturer.name || product.manufacturer.logo)
                "
                class="flex flex-col gap-[4px]"
              >
                <p class="text-grays-600 text-sm font-medium">חברה</p>
                <p class="text-grays-900 text-base font-regular">
                  <img
                    v-if="product.manufacturer.logo"
                    :alt="product.manufacturer.name"
                    :src="
                      'https://images.mishnatyosef.org/images/brands/' +
                      product.manufacturer.logo
                    "
                    class="h-[50px] object-cover max-h-full max-w-full"
                  />
                  <span v-else>
                    {{ product.manufacturer.name }}
                  </span>
                </p>
              </div>
            </div>
            <!-- Made in Israel + nutrition flags icons with labels -->
            <div
              v-if="madeInIsrael || nutritionFlagIcons.length > 0"
              class="flex flex-wrap gap-x-[20px] gap-y-[12px] mt-[28px]"
            >
              <div
                v-for="icon in nutritionFlagIcons"
                :key="icon.key"
                class="flex items-center gap-[8px]"
              >
                <img :src="icon.src" :alt="icon.label" class="w-8 h-8" />
                <span class="text-sm text-grays-900">{{ icon.label }}</span>
              </div>
              <div v-if="madeInIsrael" class="flex items-center gap-[8px]">
                <img
                  :src="madeInIsraelIcon"
                  alt="מיוצר בישראל"
                  class="w-8 h-8 object-contain"
                />
                <span class="text-sm text-grays-900">מיוצר בישראל</span>
              </div>
            </div>
            <div
              v-if="
                product.product.sub_items && product.product.sub_items.length
              "
              class="flex flex-col gap-[4px] mt-1 md:mt-[36px]"
            >
              <p class="text-grays-600 text-sm font-medium">
                בחר {{ product.attribute_name }}:
              </p>
              <radio-select
                :sizes="attributeMap"
                :sizes-not-in-stock="sizesNotInStock"
                @updateSelectedSize="handleSizeSelection"
              />
              <div
                v-if="sizesInCart.length"
                class="text-success text-sm font-medium flex items-center gap-1 mt-4"
              >
                <span class="text-success material-symbols-rounded text-sm"
                  >error</span
                >
                <template v-if="sizesInCart.length === 1"
                  >{{ product.attribute_name }}
                </template>
                <template v-if="sizesInCart.length > 1"
                  >{{ product.plural_attribute_name }}
                </template>
                {{ sizesInCart.join(",") }}
                <template v-if="sizesInCart.length === 1">הוזמן</template>
                <template v-if="sizesInCart.length > 1">הוזמנו</template>
                על ידך
              </div>
            </div>
          </div>
          <div
            :class="
              isCurrentItemInCart && buttonDisabled
                ? 'bg-primary-100'
                : 'bg-[#FBFBFB]'
            "
            class="flex flex-col lg:flex-row justify-between items-center px-4 lg:px-[8.5px] lg:pr-[48px] pb-1 lg:pb-[7.5px] pt-1 lg:pt-[7.5px] mt-1 lg:mt-[12.5px] rounded-b"
          >
            <p class="text-primary-900 text-lg font-bold my-[8px]">
              {{ product.price }} ₪
              <span class="text-sm font-regular">{{
                computedPriceForweight
              }}</span>
            </p>

            <!-- STATE 1: Not in cart -->
            <div
              v-if="!isCurrentItemInCart"
              class="flex flex-row gap-[8px] items-center"
            >
              <span
                v-if="soldOut"
                class="text-primary-700 font-medium text-base"
              >
                המוצר אזל מהמלאי
              </span>
              <input-amount
                v-model="amount"
                :disabled="soldOut"
                :max-amount="computedMaxAmount"
                :out-of-stock="
                  product.available !== null && product.available === 0
                "
                @valueChanged="handleValueChanged"
              />
              <button-cta
                :disabled="soldOut || processing"
                @click="handleAddToCart($event, product)"
              >
                הוסף להזמנה
              </button-cta>
            </div>

            <!-- STATE 2: In cart, amount unchanged — show delete + checkmark -->
            <div
              v-else-if="buttonDisabled"
              class="flex flex-row gap-[8px] items-center"
            >
              <button
                class="bg-success h-6 w-6 flex flex-none items-center justify-center rounded-full text-white"
                @click="toggleIsModalVisible"
              >
                <span class="material-symbols-rounded text-base">check</span>
              </button>
              <input-amount
                v-model="amount"
                :max-amount="computedMaxAmount"
                :out-of-stock="
                  product.available !== null && product.available === 0
                "
                class="bg-white"
                @valueChanged="handleValueChanged"
              />
              <button-text
                :disabled="processing"
                class="font-medium text-base flex items-center gap-1"
                @click="deleteProduct"
              >
                <span class="material-symbols-rounded text-xl">delete</span>
                מחק מוצר
              </button-text>
            </div>

            <!-- STATE 3: In cart, amount changed — show update action -->
            <div v-else class="flex flex-row gap-[8px] items-center">
              <input-amount
                v-model="amount"
                :max-amount="computedMaxAmount"
                :out-of-stock="
                  product.available !== null && product.available === 0
                "
                class="bg-white"
                @valueChanged="handleValueChanged"
              />
              <button-text
                :disabled="processing"
                class="text-primary-700 font-medium text-base"
                @click="updateAmount"
              >
                עדכן כמות מוצר
              </button-text>
            </div>
          </div>
        </div>
      </div>

      <!-- Product Details Tabs Section - Full Width Below Everything -->
      <modal-product-tabs :product="product" />
    </div>
  </section>
</template>

<script>
import { mapActions, mapGetters, mapMutations, mapState } from "vuex";
import { Swiper, SwiperSlide } from "swiper/vue";
import { Navigation, Pagination } from "swiper";
import axiosInstance from "../../helpers/axios.js";
import eventBus from "@/helpers/eventBus";
import { reportRemoveFailure } from "@/helpers/removeItemFailure";
import { isSoldOut, variantIsSoldOut } from "@/helpers/stock";
import ButtonLike from "@/components/Global/ButtonLike.vue";
import ButtonText from "@/components/Global/ButtonText.vue";
import InputAmount from "@/components/Global/InputAmount.vue";
import ButtonCta from "@/components/Global/ButtonCTA.vue";
import RadioSelect from "@/components/Global/RadioSelect.vue";
import BillingProductsAgreement from "@/components/Modals/BillingProductsAgreement.vue";

export default {
  name: "modal-product",
  components: {
    BillingProductsAgreement,
    RadioSelect,
    ButtonCta,
    InputAmount,
    ButtonText,
    ButtonLike,
    Swiper,
    SwiperSlide,
  },
  data() {
    return {
      amount: 1,
      maxAmount: null,
      buttonDisabled: true,
      selectedSize: null,
      sizesInCart: [],
      attributeMap: [],
      sizesNotInStock: [],
      update: false,
      image: "",
      imageList: [],
      description: "",
      showBillingProductAgreement: false,
      processing: false,
    };
  },
  methods: {
    // A child item without its own description falls back to the parent ("father") description.
    resolveDescription(childItem = null) {
      const candidates = [
        childItem?.description,
        childItem?.product?.description,
        this.product?.description,
        this.product?.product?.description,
      ];
      return (
        candidates.find((text) => typeof text === "string" && text.trim()) || ""
      );
    },
    async deleteProduct() {
      this.processing = true;
      const itemToDelete =
        this.cart.find(
          (item) =>
            item.item_salesID ===
            this.product?.product?.sub_items.find(
              (item) => item.attribute === this.selectedSize
            )?.item_salesID
        ) || this.product;
      try {
        await axiosInstance.post(
          `/remove_item/${this.$route.params.orderID}/${
            itemToDelete?.item_salesID || itemToDelete.id
          }`,
          itemToDelete
        );
        this.removeFromCart(itemToDelete);
        this.product.amount = 1;
        await this.showToast({
          message:
            itemToDelete.full_name ||
            itemToDelete?.product?.full_name ||
            itemToDelete?.name,
          type: "danger",
        });
        await this.toggleIsModalVisible();
        this.processing = false;
      } catch (e) {
        this.processing = false;
        reportRemoveFailure(e, this.showToast);
      }
    },
    toggleShowBillingProductModal() {
      this.showBillingProductAgreement = !this.showBillingProductAgreement;
    },
    ...mapMutations({
      removeFromCart: "cart/removeFromCart",
    }),
    ...mapActions({
      toggleIsModalVisible: "productModal/toggleIsModalVisible",
      addToCart: "cart/addToCart",
      showToast: "orderToast/showToast",
      updateAmountInCart: "cart/updateAmount",
    }),
    async toggleLike(product) {
      product.favourite = product.favourite === 1 ? 0 : 1;
      try {
        await axiosInstance.get(`/toggle_favourites/${product.id}`);
      } catch (e) {
        console.error(e);
      }
    },
    handleSizeSelection(size) {
      this.selectedSize = size;
    },
    async handleAddToCart(event) {
      event.stopPropagation();
      if (this.soldOut) {
        this.showToast({ message: "המוצר אזל מהמלאי", type: "error" });
        return;
      }
      this.processing = true;
      if (!this.billingProductIsAgreed && !this.cart.length) {
        this.toggleShowBillingProductModal();
        this.processing = false;
        return;
      }

      await this.continueAddToCart();
    },
    async continueAddToCart() {
      let newProduct = {
        ...this.product,
        amount: this.amount,
        price1: this.product.price,
      };
      if (
        this.product.product.sub_items &&
        this.product.product.sub_items.length
      ) {
        const selectedSubItem = Object.values(
          this.product?.selected_subitems ||
            this.product?.product?.sub_items ||
            {}
        ).find((item) => item.attribute === this.selectedSize);

        if (selectedSubItem) {
          newProduct = {
            ...selectedSubItem,
            amount: this.amount,
            price1: selectedSubItem.price,
            featured_image:
              selectedSubItem.featured_image || this.product.featured_image,
          };
        }
      }
      try {
        const { data } = await axiosInstance.post(
          `/add_item/${this.$route.params.orderID}`,
          {
            itemID: newProduct.id,
            amount: newProduct.amount,
            name: newProduct.name,
            item_salesID: newProduct.item_salesID,
          }
        );
        this.product.amount = this.amount;
        this.addToCart(newProduct);

        if (data.length > 1) {
          data.pop();
          data.forEach((item) => {
            this.addToCart({ specialProduct: true, ...item });
          });
        }
        this.processing = false;
        this.toggleIsModalVisible();
        this.showToast({
          message: newProduct.name,
          type: "add",
          amount: this.amount,
        });
      } catch (error) {
        this.showToast({
          message: error.response?.data?.message,
          type: "error",
        });
        this.processing = false;
        if (error.response?.data?.message === "המכירה סגורה להזמנות.") {
          setTimeout(() => {
            this.$router.push("/");
          }, 3000);
        }
        // This product belongs to a sale other than the one the order is
        // for - the list behind the modal is stale. Close it and let the
        // page refill itself.
        if (error.response?.data?.code === "sale_mismatch") {
          this.toggleIsModalVisible();
          eventBus.emit("saleMismatch");
        }
      }
    },
    async updateAmount() {
      this.processing = true;
      let itemToUpdate;
      if (this.selectedSize) {
        const selectedSubItem = this.cartProducts.find(
          (item) =>
            item.attribute === this.selectedSize &&
            item.parent_item === this.product.id
        );
        itemToUpdate = selectedSubItem || this.product;
      } else {
        itemToUpdate = this.cartProducts.find(
          (item) =>
            (item?.item_sale?.product?.id || item.id) === this.product.id
        );
      }
      itemToUpdate.amount = this.amount;
      try {
        await axiosInstance.post(
          `/update_item/${this.$route.params.orderID}`,
          itemToUpdate
        );
        await this.updateAmountInCart({
          id: itemToUpdate.id,
          amount: this.amount,
        });
        this.product.amount = this.amount;
        this.buttonDisabled = true;
        await this.toggleIsModalVisible();
        this.processing = false;
        this.showToast({
          message: itemToUpdate.name,
          type: "update",
          amount: this.amount,
        });
      } catch (e) {
        await this.showToast({
          message: e.response.data.message,
          type: "error",
        });
        if (e.response.data.message === "המכירה סגורה להזמנות.") {
          setTimeout(() => {
            this.$router.push("/");
          }, 3000);
        }

        this.processing = false;
        console.error(e);
      }
    },
    handleValueChanged() {
      if (this.selectedSize) {
        const selectedSubItem = this.cartProducts.find(
          (item) =>
            item.attribute === this.selectedSize &&
            item.parent_item === this.product.id
        );
        this.buttonDisabled = this.amount === selectedSubItem?.amount;
      } else {
        this.buttonDisabled = this.product.amount === this.amount;
      }
    },
    closeModal(event) {
      const modalContent = document.querySelector(".modal-content");
      if (modalContent && !modalContent.contains(event.target)) {
        this.toggleIsModalVisible();
      }
    },
  },
  computed: {
    nutritionFlagIcons() {
      const fsrKeyMap = {
        FSR2: {
          key: "high_sodium",
          src: new URL(
            "@/assets/icons/product/high-sodium.svg",
            import.meta.url
          ).href,
          label: "נתרן בכמות גבוהה",
        },
        FSR3: {
          key: "high_sugar",
          src: new URL("@/assets/icons/product/high-sugar.svg", import.meta.url)
            .href,
          label: "סוכר בכמות גבוהה",
        },
        FSR4: {
          key: "high_fat",
          src: new URL("@/assets/icons/product/high-fat.svg", import.meta.url)
            .href,
          label: "שומן רווי בכמות גבוהה",
        },
      };
      const nutritionFlags =
        this.product?.product?.barcodes?.[0]?.nutrition_flags ?? [];
      return nutritionFlags
        .map((flagEntry) => {
          const fsrKey = flagEntry?.nutrition_flag?.key;
          return fsrKeyMap[fsrKey] || null;
        })
        .filter(Boolean);
    },
    madeInIsrael() {
      return !!this.product?.product?.made_in_israel;
    },
    madeInIsraelIcon() {
      return new URL(
        "@/assets/icons/product/made-in-israel.png",
        import.meta.url
      ).href;
    },
    ...mapGetters({
      noLimit: "user/getNoLimit",
    }),
    // The signal the product card renders as "אזל מהמלאי": a plain
    // product's own flag, or the picked size of a variant product.
    soldOut() {
      if (this.product?.product?.sub_items?.length) {
        return (
          this.selectedSize !== null &&
          this.sizesNotInStock.includes(this.selectedSize)
        );
      }
      return isSoldOut(this.product);
    },
    computedMaxAmount() {
      if (isSoldOut(this.product)) {
        return this.product.amount;
      } else if (this.noLimit !== 0) {
        return 99999;
      } else {
        return this.product.max_per_man;
      }
    },

    computedUnit() {
      if (!this.product?.item_type) {
        return undefined;
      }
      const type = this.product.item_type
        ? `${this.product.item_type.name} `
        : "";
      const units = this.product.units > 0 ? `${this.product.units} ` : "";
      const approx = this.product.approx !== 0 ? " כ-" : "";
      const unit_type = this.product.item_unit
        ? this.product.item_unit.name
        : "";
      return `${type}${approx}${units}${unit_type}`;
    },
    computedPriceForweight() {
      const units = this.product.units > 0 ? `${this.product.units} ` : "";
      const unit_type = this.product.item_unit
        ? this.product.item_unit.name
        : "";
      let price = "";
      if (Number(this.product.units) !== 0) {
        if (this.product.units_type == 10 && this.product.units < 1) {
          price =
            " (₪" +
            parseFloat(this.product.price / units / 10).toFixed(2) +
            " ל-100 גרם)";
        } else if (this.product.units_type == 16 && this.product.units < 1) {
          price =
            " (₪" +
            parseFloat(this.product.price / units / 10).toFixed(2) +
            " ל-100 מ״ל)";
        } else {
          price =
            " (₪" +
            parseFloat(this.product.price / units).toFixed(2) +
            " ל" +
            unit_type +
            ")";
        }
      }
      return price;
    },
    ...mapState({
      cart: (state) => state.cart.cart,
      product: (state) => state.productModal.product,
      cartProducts: (state) => state.cart.cart,
      billingProductIsAgreed: (state) => state.order.billingProductIsAgreed,
    }),
    isCurrentItemInCart() {
      return this.update || this.isInCart(this.product);
    },
    ...mapGetters({
      isInCart: "cart/isInCart",
    }),
  },
  watch: {
    selectedSize() {
      const itemSelect = this.product?.product?.sub_items.find(
        (item) => item.attribute === this.selectedSize
      );
      const selectedSubItem = Object.values(
        this.product?.selected_subitems || {}
      ).find((item) => item.attribute === this.selectedSize);
      this.maxAmount = selectedSubItem
        ? selectedSubItem.max_per_man || 1
        : this.product.max_per_man || 1;
      this.amount =
        this.cart.find(
          (item) =>
            item.item_salesID ===
            this.product?.product?.sub_items.find(
              (item) => item.attribute === this.selectedSize
            )?.item_salesID
        )?.amount || 1;
      this.update = this.sizesInCart.includes(this.selectedSize);
      this.buttonDisabled = true;
      this.product.price = this.product.product.sub_items
        ? this.product.product.sub_items.find(
            (item) => item.attribute === this.selectedSize
          ).price
        : this.product.price;
      const childItem = this.product.product.sub_items.find(
        (item) => item.attribute === this.selectedSize
      );
      this.description = this.resolveDescription(childItem);

      this.image = this.product.product.featured_image;
      if (
        childItem &&
        childItem.hasOwnProperty("featured_image") &&
        childItem.featured_image !== "no-image.png"
      ) {
        this.image = childItem.featured_image;
      }
      if (
        childItem &&
        childItem.hasOwnProperty("product") &&
        childItem.product.featured_image !== "no-image.png"
      ) {
        this.image = childItem.product.featured_image;
      }

      this.imageList = [
        {
          filename: this.image.split(".")[0],
          extension: this.image.split(".")[1],
        },
      ];
    },
  },
  mounted() {
    this.image = this.product.product.featured_image;
    this.description = this.resolveDescription();
    this.imageList = this.product.product.images;
    this.amount = this.product.amount || 1;
    if (this.product.product.sub_items) {
      this.attributeMap = [
        ...new Set(
          this.product.product.sub_items.map((item) => item.attribute)
        ),
      ];
      this.sizesNotInStock = this.attributeMap.filter((attribute) =>
        variantIsSoldOut(this.product, attribute)
      );

      this.amount = this.product.amount || 1;

      const childs = this.product.product.sub_items.map((item) => ({
        id: item.id,
        attribute: item.attribute,
      }));
      childs.forEach((item) => {
        if (this.isInCart(item)) {
          this.sizesInCart.push(item.attribute);
        }
      });
    }
  },
  setup() {
    return {
      modules: [Navigation, Pagination],
    };
  },
};
</script>

<style scoped>
/* Add scoped styles if necessary */
</style>
